import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertVariants = cva(
  "relative w-full rounded-lg border p-4",
  {
    variants: {
      layout: {
        // shadcn's original: the icon is lifted out of flow and pinned to the
        // padding box, and the description is padded and nudged to clear it.
        stacked:
          "[&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4",
        // Icon stays in flow as a flex item, so the consumer's own alignment
        // (items-center / items-start) actually governs it. `stacked` cannot do
        // this: an absolutely-positioned icon ignores `items-center`, and the
        // -3px nudge then pulls the text off the icon's centre line.
        row: "flex gap-3 [&>svg]:shrink-0",
      },
      variant: {
        default:
          "bg-background text-foreground border-border [&>svg]:text-foreground",
        destructive:
          "bg-destructive-soft text-destructive-soft-foreground border-destructive/30 [&>svg]:text-destructive",
        success:
          "bg-success-soft text-success-soft-foreground border-success/30 [&>svg]:text-success",
        warning:
          "bg-warning-soft text-warning-soft-foreground border-warning/30 [&>svg]:text-warning",
        info:
          "bg-info-soft text-info-soft-foreground border-info/30 [&>svg]:text-info",
      },
    },
    defaultVariants: {
      layout: "stacked",
      variant: "default",
    },
  }
)

/**
 * Inline callout banner in default, destructive, success, warning and info variants; composed with AlertTitle and AlertDescription. Use `layout="row"` when the icon should sit in flow beside the content rather than pinned to the top-left corner.
 *
 * @category Feedback
 */
const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, layout, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant, layout }), className)}
    {...props}
  />
))
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
